(function () {
  'use strict';

  const STORAGE_KEY = 'sv_vat_mode';
  const DEFAULT_MODE = 'inc';
  const DEFAULT_VAT_RATE = 21;

  /*
   * Hier bewaren we de originele zichtbare EX-btw tekst.
   * Daardoor wordt een prijs NOOIT twee keer ×1,21 gedaan.
   */
  const originalPriceText = new WeakMap();

  function getStoredMode() {
    try {
      const value = localStorage.getItem(STORAGE_KEY);

      if (value === 'inc' || value === 'ex') {
        return value;
      }
    } catch (error) {}

    return DEFAULT_MODE;
  }

  function storeMode(mode) {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (error) {}
  }

  function getCurrency() {
    if (
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
    ) {
      return window.Shopify.currency.active;
    }

    return 'EUR';
  }

  function getLocale() {
    return document.documentElement.lang || 'nl-NL';
  }

  function getCurrencySymbol() {
    try {
      const parts = new Intl.NumberFormat(getLocale(), {
        style: 'currency',
        currency: getCurrency()
      }).formatToParts(1);

      const currencyPart = parts.find(function (part) {
        return part.type === 'currency';
      });

      if (currencyPart && currencyPart.value) {
        return currencyPart.value;
      }
    } catch (error) {}

    if (getCurrency() === 'EUR') {
      return '€';
    }

    return getCurrency();
  }

  function escapeRegExp(value) {
    return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /*
   * Zet een zichtbaar bedrag zoals:
   *
   * €12,10
   * € 12,10
   * 12,10 €
   * EUR 12,10
   *
   * om naar centen.
   */
  function parseMoneyAmount(value) {
    let cleaned = String(value)
      .replace(/\s|\u00A0/g, '')
      .replace(/[^\d,.-]/g, '');

    if (!cleaned) {
      return null;
    }

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    let decimalSeparator = null;

    if (lastComma > -1 && lastDot > -1) {
      decimalSeparator =
        lastComma > lastDot ? ',' : '.';
    } else if (lastComma > -1) {
      const decimals =
        cleaned.length - lastComma - 1;

      if (decimals === 1 || decimals === 2) {
        decimalSeparator = ',';
      }
    } else if (lastDot > -1) {
      const decimals =
        cleaned.length - lastDot - 1;

      if (decimals === 1 || decimals === 2) {
        decimalSeparator = '.';
      }
    }

    if (decimalSeparator) {
      const separatorIndex =
        cleaned.lastIndexOf(decimalSeparator);

      const integerPart = cleaned
        .slice(0, separatorIndex)
        .replace(/[.,]/g, '');

      let decimalPart = cleaned
        .slice(separatorIndex + 1)
        .replace(/[.,]/g, '');

      if (decimalPart.length === 1) {
        decimalPart += '0';
      }

      decimalPart = decimalPart.slice(0, 2);

      const amount =
        Number(integerPart || '0') +
        Number(decimalPart || '0') / 100;

      if (!Number.isFinite(amount)) {
        return null;
      }

      return Math.round(amount * 100);
    }

    cleaned = cleaned.replace(/[.,]/g, '');

    const amount = Number(cleaned);

    if (!Number.isFinite(amount)) {
      return null;
    }

    return Math.round(amount * 100);
  }

  window.ShopVAT = {
    mode: getStoredMode(),

    getMode() {
      return this.mode;
    },

    setMode(mode) {
      if (mode !== 'inc' && mode !== 'ex') {
        return;
      }

      this.mode = mode;
      storeMode(mode);

      document.documentElement.setAttribute(
        'data-vat-mode',
        mode
      );

      this.update();

      document.dispatchEvent(
        new CustomEvent('sv:vat-change', {
          detail: {
            mode: mode
          }
        })
      );
    },

    /*
     * Shopify systeemprijs = EXCLUSIEF btw.
     */
    calculateIncVat(cents, vatRate) {
      const rate = Number(
        vatRate || DEFAULT_VAT_RATE
      );

      return Math.round(
        Number(cents) * (1 + rate / 100)
      );
    },

    formatMoney(cents) {
      const amount = Number(cents) / 100;

      try {
        return new Intl.NumberFormat(getLocale(), {
          style: 'currency',
          currency: getCurrency()
        }).format(amount);
      } catch (error) {
        return (
          '€' +
          amount
            .toFixed(2)
            .replace('.', ',')
        );
      }
    },

    /*
     * Zet alle geldbedragen binnen één tekstfragment om.
     *
     * Bijvoorbeeld:
     * "Vanaf €10,00"
     *
     * wordt:
     * "Vanaf €12,10"
     */
    transformMoneyText(text, mode, vatRate) {
      if (!text) {
        return text;
      }

      if (mode === 'ex') {
        return text;
      }

      const currency = getCurrency();
      const symbol = getCurrencySymbol();

      const currencyTokens = [
        symbol,
        currency
      ]
        .filter(Boolean)
        .filter(function (value, index, array) {
          return array.indexOf(value) === index;
        })
        .map(escapeRegExp)
        .join('|');

      if (!currencyTokens) {
        return text;
      }

      /*
       * Ondersteunt o.a.:
       *
       * €12,10
       * € 12,10
       * 12,10 €
       * EUR 12,10
       * 1.234,56 €
       */
      const amountPattern =
        '(?:\\d{1,3}(?:[.\\s\\u00A0]\\d{3})+|\\d+)(?:[,.]\\d{1,2})?';

      const pattern = new RegExp(
        '(?:' +
          '(' + currencyTokens + ')' +
          '\\s*' +
          '(' + amountPattern + ')' +
        '|' +
          '(' + amountPattern + ')' +
          '\\s*' +
          '(' + currencyTokens + ')' +
        ')',
        'gi'
      );

      const self = this;

      return text.replace(
        pattern,
        function (
          fullMatch,
          prefixCurrency,
          prefixAmount,
          suffixAmount,
          suffixCurrency
        ) {
          const rawAmount =
            prefixAmount || suffixAmount;

          const cents =
            parseMoneyAmount(rawAmount);

          if (
            cents === null ||
            !Number.isFinite(cents)
          ) {
            return fullMatch;
          }

          const incCents =
            self.calculateIncVat(
              cents,
              vatRate
            );

          return self.formatMoney(incCents);
        }
      );
    },

    /*
     * Dit blijft de meest betrouwbare methode.
     *
     * data-price-ex bevat de Shopify systeemprijs
     * in centen en die systeemprijs is EX btw.
     */
    updateExplicitPrices(root) {
      const mode = this.mode;

      root
        .querySelectorAll('[data-vat-price]')
        .forEach((element) => {
          const rawExPrice =
            element.getAttribute('data-price-ex') ??
            element.getAttribute('data-price-inc');

          const exPrice =
            Number(rawExPrice);

          const vatRate =
            Number(
              element.getAttribute(
                'data-vat-rate'
              ) || DEFAULT_VAT_RATE
            );

          if (!Number.isFinite(exPrice)) {
            return;
          }

          const displayedPrice =
            mode === 'inc'
              ? this.calculateIncVat(
                  exPrice,
                  vatRate
                )
              : exPrice;

          element.textContent =
            this.formatMoney(
              displayedPrice
            );
        });
    },

    /*
     * NIEUW:
     *
     * Automatische fallback voor bestaande theme-prijzen.
     *
     * Hierdoor hoeven bestaande prijsblokken niet allemaal
     * handmatig data-vat-price te krijgen.
     *
     * We zoeken alleen in elementen die duidelijk met prijs
     * te maken hebben.
     */
    updateAutomaticPrices(root) {
      const self = this;
      const mode = this.mode;

      const selector = [
        '.price',
        '.old-price',
        '.money',
        '.product-price',
        '[class*="price"]',
        '[id*="price"]'
      ].join(',');

      let containers = [];

      if (
        root !== document &&
        root.matches &&
        root.matches(selector)
      ) {
        containers.push(root);
      }

      if (root.querySelectorAll) {
        containers =
          containers.concat(
            Array.from(
              root.querySelectorAll(selector)
            )
          );
      }

      const handledNodes = new Set();

      containers.forEach((container) => {
        /*
         * Expliciete VAT-markup wordt al hierboven
         * behandeld en mag niet nogmaals worden omgerekend.
         */
        if (
          container.matches('[data-vat-price]')
        ) {
          return;
        }

        if (
          container.closest('[data-vat-ignore]')
        ) {
          return;
        }

        const walker =
          document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT
          );

        let node;

        while ((node = walker.nextNode())) {
          if (handledNodes.has(node)) {
            continue;
          }

          handledNodes.add(node);

          const parent =
            node.parentElement;

          if (!parent) {
            continue;
          }

          /*
           * Niet in scripts, styles, formulieren etc.
           */
          if (
            parent.closest(
              'script, style, noscript, template, input, textarea, select, option'
            )
          ) {
            continue;
          }

          /*
           * Expliciet VAT element?
           * Dan NIET automatisch nogmaals aanpassen.
           */
          if (
            parent.closest('[data-vat-price]')
          ) {
            continue;
          }

          const currentText =
            node.nodeValue;

          /*
           * Alleen tekst waar daadwerkelijk een valuta
           * in voorkomt is interessant.
           */
          const symbol =
            getCurrencySymbol();

          const currency =
            getCurrency();

          if (
            !currentText.includes(symbol) &&
            !currentText
              .toUpperCase()
              .includes(
                currency.toUpperCase()
              )
          ) {
            continue;
          }

          /*
           * Eerste keer:
           * huidige storefronttekst = oorspronkelijke
           * Shopify EX-btw prijs.
           */
          if (
            !originalPriceText.has(node)
          ) {
            originalPriceText.set(
              node,
              currentText
            );
          }

          const exText =
            originalPriceText.get(node);

          const output =
            mode === 'inc'
              ? self.transformMoneyText(
                  exText,
                  'inc',
                  DEFAULT_VAT_RATE
                )
              : exText;

          if (node.nodeValue !== output) {
            node.nodeValue = output;
          }
        }
      });
    },

    updateLabels(root) {
      const mode = this.mode;

      root
        .querySelectorAll('[data-vat-label]')
        .forEach((element) => {
          element.textContent =
            mode === 'ex'
              ? 'excl. btw'
              : 'incl. btw';
        });
    },

    updateSwitches(root) {
      const mode = this.mode;

      root
        .querySelectorAll(
          '[data-vat-switch], .svo24p-price-switch'
        )
        .forEach((switcher) => {
          switcher.setAttribute(
            'data-mode',
            mode
          );

          switcher
            .querySelectorAll(
              '[data-vat-mode]'
            )
            .forEach((button) => {
              const buttonMode =
                button.getAttribute(
                  'data-vat-mode'
                );

              button.classList.toggle(
                'is-active',
                buttonMode === mode
              );

              button.setAttribute(
                'aria-pressed',
                buttonMode === mode
                  ? 'true'
                  : 'false'
              );
            });
        });
    },

    update(root) {
      root = root || document;

      /*
       * Eerst de expliciete betrouwbare markup.
       */
      this.updateExplicitPrices(root);

      /*
       * Daarna alle overige theme-prijzen automatisch.
       */
      this.updateAutomaticPrices(root);

      this.updateLabels(root);
      this.updateSwitches(root);
    },

    init() {
      document.documentElement.setAttribute(
        'data-vat-mode',
        this.mode
      );

      /*
       * Centrale Incl. / Excl. click handler.
       */
      document.addEventListener(
        'click',
        (event) => {
          const button =
            event.target.closest(
              '[data-vat-mode]'
            );

          if (!button) {
            return;
          }

          const mode =
            button.getAttribute(
              'data-vat-mode'
            );

          this.setMode(mode);
        }
      );

      this.update();

      /*
       * Predictive search, AJAX cart,
       * recommendation sliders,
       * collection call-outs,
       * quick shop etc.
       */
      let queued = false;

      const observer =
        new MutationObserver(() => {
          if (queued) {
            return;
          }

          queued = true;

          requestAnimationFrame(() => {
            queued = false;

            this.update();
          });
        });

      observer.observe(
        document.body,
        {
          childList: true,
          subtree: true
        }
      );
    }
  };

  function initVAT() {
    if (window.ShopVATInitialized) {
      window.ShopVAT.update();
      return;
    }

    window.ShopVATInitialized = true;

    window.ShopVAT.init();
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initVAT
    );
  } else {
    initVAT();
  }

  /*
   * Shopify Theme Editor /
   * dynamisch geladen sections.
   */
  document.addEventListener(
    'shopify:section:load',
    function (event) {
      if (window.ShopVAT) {
        window.ShopVAT.update(
          event.target
        );
      }
    }
  );
})();